import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Plus, Trash2, Edit, Save, X, Upload, Download, Search, 
  Filter, Trophy, Calendar, User, Hash, FileSpreadsheet, 
  CheckCircle, AlertCircle, Eye, EyeOff, Dribbble, 
} from "lucide-react";
import { Button } from "../ui/button";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const getAuthHeader = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Predefined sport categories
const SPORT_CATEGORIES = [
  { id: "mens_college_basketball", label: "Mens College Basketball", icon: "🏀", short: "MCB" },
  { id: "womens_college_basketball", label: "Womens College Basketball", icon: "🏀", short: "WCB" },
  { id: "boys_hs_basketball", label: "Boys High School Basketball", icon: "🏀", short: "BHS" },
  { id: "girls_hs_basketball", label: "Girls High School Basketball", icon: "🏀", short: "GHS" },
  { id: "college_football", label: "College Football", icon: "🏈", short: "CFB" },
  { id: "hs_football", label: "High School Football", icon: "🏈", short: "HSF" },
];

export const RecordBookTab = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState(SPORT_CATEGORIES[0].id);
  const [saving, setSaving] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  // Get current sport label
  const currentSport = SPORT_CATEGORIES.find(s => s.id === activeTab);

  const [form, setForm] = useState({
    sport: currentSport?.label || "",
    date: "",
    player: "",
    total: "",
    category: "",
    notes: ""
  });

  useEffect(() => {
    fetchRecords();
  }, [activeTab]);

  // Update form sport when tab changes
  useEffect(() => {
    const sport = SPORT_CATEGORIES.find(s => s.id === activeTab);
    if (sport && !editing) {
      setForm(prev => ({ ...prev, sport: sport.label }));
    }
  }, [activeTab, editing]);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const sportLabel = SPORT_CATEGORIES.find(s => s.id === activeTab)?.label;
      const params = sportLabel ? `?sport=${encodeURIComponent(sportLabel)}` : "";
      const response = await axios.get(`${API}/records/admin/all${params}`, { headers: getAuthHeader() });
      setRecords(response.data || []);
    } catch (error) {
      console.error("Error fetching records:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    // Ensure the sport is set to current tab's sport label
    const sportLabel = SPORT_CATEGORIES.find(s => s.id === activeTab)?.label || form.sport;
    const submitData = { ...form, sport: sportLabel };
    
    try {
      if (editing) {
        await axios.put(`${API}/records/${editing}`, submitData, { headers: getAuthHeader() });
      } else {
        await axios.post(`${API}/records`, submitData, { headers: getAuthHeader() });
      }
      
      setShowForm(false);
      setEditing(null);
      setForm({ sport: sportLabel, date: "", player: "", total: "", category: "", notes: "" });
      fetchRecords();
    } catch (error) {
      console.error("Error saving record:", error);
      alert("Failed to save record");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (record) => {
    setEditing(record.id);
    setForm({
      sport: record.sport || "",
      date: record.date || "",
      player: record.player || "",
      total: record.total || "",
      category: record.category || "",
      notes: record.notes || ""
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    
    try {
      await axios.delete(`${API}/records/${id}`, { headers: getAuthHeader() });
      fetchRecords();
    } catch (error) {
      console.error("Error deleting record:", error);
    }
  };

  const handleTogglePublish = async (id) => {
    try {
      await axios.post(`${API}/records/${id}/toggle-publish`, {}, { headers: getAuthHeader() });
      fetchRecords();
    } catch (error) {
      console.error("Error toggling publish:", error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImportResult(null);
    const formData = new FormData();
    formData.append('file', file);
    
    // Get current sport for this tab
    const sportLabel = SPORT_CATEGORIES.find(s => s.id === activeTab)?.label;
    if (sportLabel) {
      formData.append('sport_override', sportLabel);
    }
    
    try {
      const response = await axios.post(`${API}/records/import/csv`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setImportResult(response.data);
      fetchRecords();
    } catch (error) {
      setImportResult({
        success: false,
        error: error.response?.data?.detail || "Import failed"
      });
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const sportLabel = SPORT_CATEGORIES.find(s => s.id === activeTab)?.label || "Sport";
    // Template matches user's format: Sport, Date, Player / Team, Record Category, Total
    const csv = `Sport,Date,Player / Team,Record Category,Total
${sportLabel},,,"Points (Player)",
${sportLabel},,,"Points (Team)",
${sportLabel},,,"Field Goals Made",
${sportLabel},,,"Three-Pointers Made",
${sportLabel},,,"Free Throws Made",
${sportLabel},,,"Rebounds (Total)",
${sportLabel},,,"Assists",
${sportLabel},,,"Steals",
${sportLabel},,,"Blocks",`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ASN_Single_Game_Record_Book_-_${sportLabel.replace(/[^a-zA-Z0-9]/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredRecords = records.filter(record => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      record.player?.toLowerCase().includes(query) ||
      record.total?.toLowerCase().includes(query) ||
      record.category?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading uppercase text-white">Record Book</h2>
          <p className="text-sm text-zinc-500 mt-1">Manage ASN broadcast records by sport</p>
        </div>
      </div>

      {/* Sport Tabs */}
      <div className="bg-zinc-900 border border-white/10 p-1 flex flex-wrap gap-1" data-testid="sport-tabs">
        {SPORT_CATEGORIES.map((sport) => (
          <button
            key={sport.id}
            onClick={() => { setActiveTab(sport.id); setShowForm(false); setImportResult(null); }}
            className={`flex items-center gap-2 px-4 py-2.5 text-xs font-bold uppercase transition-all ${
              activeTab === sport.id
                ? 'bg-orange-500 text-black'
                : 'bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700'
            }`}
            data-testid={`tab-${sport.id}`}
          >
            <span className="text-sm">{sport.icon}</span>
            <span className="hidden lg:inline">{sport.label}</span>
            <span className="lg:hidden">{sport.short}</span>
          </button>
        ))}
      </div>

      {/* Current Sport Header & Actions */}
      <div className="flex items-center justify-between bg-zinc-800/50 border border-white/10 p-4">
        <div className="flex items-center gap-3">
          <span className="text-3xl">{currentSport?.icon}</span>
          <div>
            <h3 className="text-lg font-heading uppercase text-white">{currentSport?.label}</h3>
            <p className="text-xs text-zinc-500">{filteredRecords.length} records in this category</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={downloadTemplate}
            className="bg-zinc-700 text-white hover:bg-zinc-600 text-xs px-4 py-2 h-auto rounded-none flex items-center gap-2"
            data-testid="download-template"
          >
            <Download className="w-4 h-4" /> Template
          </Button>
          <label className="cursor-pointer">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="hidden"
              data-testid="csv-file-input"
            />
            <Button
              as="span"
              className="bg-green-600 text-white hover:bg-green-500 text-xs px-4 py-2 h-auto rounded-none flex items-center gap-2"
              data-testid="import-csv-btn"
            >
              <Upload className="w-4 h-4" /> Import CSV
            </Button>
          </label>
          <Button
            onClick={() => { 
              setShowForm(true); 
              setEditing(null); 
              setForm({ sport: currentSport?.label || "", date: "", player: "", total: "", category: "", notes: "" }); 
            }}
            className="bg-orange-500 text-black hover:bg-orange-400 text-xs px-4 py-2 h-auto rounded-none flex items-center gap-2"
            data-testid="add-record-btn"
          >
            <Plus className="w-4 h-4" /> Add Record
          </Button>
        </div>
      </div>

      {/* Import Result */}
      {importResult && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded border ${importResult.success ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}
        >
          <div className="flex items-start gap-3">
            {importResult.success ? (
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            )}
            <div>
              {importResult.success ? (
                <>
                  <p className="text-green-400 font-medium">Import Successful!</p>
                  <p className="text-sm text-zinc-400">
                    Imported {importResult.imported} records, skipped {importResult.skipped}
                  </p>
                  {importResult.errors?.length > 0 && (
                    <div className="mt-2 text-xs text-zinc-500">
                      <p>Errors:</p>
                      {importResult.errors.map((err, i) => (
                        <p key={i}>• {err}</p>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-red-400">{importResult.error}</p>
              )}
            </div>
            <button onClick={() => setImportResult(null)} className="ml-auto text-zinc-500 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      )}

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={`Search ${currentSport?.label || ''} records...`}
            className="w-full bg-zinc-900 border border-white/10 text-white pl-10 pr-4 py-2 text-sm"
            data-testid="search-records"
          />
        </div>
      </div>

      {/* Add/Edit Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-zinc-900 border border-orange-500/30 p-6"
            data-testid="record-form"
          >
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <span className="text-xl">{currentSport?.icon}</span>
              {editing ? "Edit Record" : `Add New ${currentSport?.label} Record`}
            </h3>
            <form onSubmit={handleSubmit} className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="col-span-2 md:col-span-1">
                <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Sport</label>
                <input
                  type="text"
                  value={currentSport?.label || form.sport}
                  disabled
                  className="w-full bg-zinc-800 border border-white/10 text-zinc-400 px-4 py-2 cursor-not-allowed"
                />
                <p className="text-[10px] text-zinc-600 mt-1">Auto-set based on selected tab</p>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Date</label>
                <input
                  type="text"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  placeholder="e.g., 2024-01-15 or Jan 15, 2024"
                  className="w-full bg-black border border-white/10 text-white px-4 py-2 focus:border-orange-500"
                  data-testid="form-date"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Player *</label>
                <input
                  type="text"
                  value={form.player}
                  onChange={(e) => setForm({ ...form, player: e.target.value })}
                  placeholder="e.g., John Smith"
                  className="w-full bg-black border border-white/10 text-white px-4 py-2 focus:border-orange-500"
                  required
                  data-testid="form-player"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Total/Record *</label>
                <input
                  type="text"
                  value={form.total}
                  onChange={(e) => setForm({ ...form, total: e.target.value })}
                  placeholder="e.g., 45 pts, 12 TDs"
                  className="w-full bg-black border border-white/10 text-white px-4 py-2 focus:border-orange-500"
                  required
                  data-testid="form-total"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Category</label>
                <input
                  type="text"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  placeholder="e.g., Single Game, Career, Season"
                  className="w-full bg-black border border-white/10 text-white px-4 py-2 focus:border-orange-500"
                  data-testid="form-category"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Notes</label>
                <input
                  type="text"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Additional notes"
                  className="w-full bg-black border border-white/10 text-white px-4 py-2 focus:border-orange-500"
                  data-testid="form-notes"
                />
              </div>
              <div className="col-span-full flex justify-end gap-3 mt-4">
                <Button
                  type="button"
                  onClick={() => { setShowForm(false); setEditing(null); }}
                  className="bg-zinc-800 text-white hover:bg-zinc-700 text-xs px-4 py-2 h-auto rounded-none"
                  data-testid="form-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-orange-500 text-black hover:bg-orange-400 text-xs px-4 py-2 h-auto rounded-none flex items-center gap-2"
                  data-testid="form-submit"
                >
                  <Save className="w-4 h-4" /> {saving ? "Saving..." : editing ? "Update" : "Add Record"}
                </Button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Records Table */}
      {loading ? (
        <div className="text-center py-12 text-zinc-500">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          Loading {currentSport?.label} records...
        </div>
      ) : filteredRecords.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900/50 border border-white/10">
          <span className="text-5xl mb-4 block">{currentSport?.icon}</span>
          <p className="text-zinc-400 text-lg">No {currentSport?.label} records yet</p>
          <p className="text-sm text-zinc-600 mt-2">Import a CSV or add records manually using the buttons above</p>
        </div>
      ) : (
        <div className="bg-zinc-900 border border-white/10 overflow-hidden" data-testid="records-table">
          <table className="w-full">
            <thead>
              <tr className="bg-zinc-800 text-xs uppercase text-zinc-400">
                <th className="text-left px-4 py-3">Player</th>
                <th className="text-left px-4 py-3">Record</th>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-4 py-3">Category</th>
                <th className="text-center px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.map((record) => (
                <tr key={record.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`record-row-${record.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-zinc-500" />
                      <span className="text-white font-medium">{record.player}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-orange-500 font-bold">{record.total}</span>
                  </td>
                  <td className="px-4 py-3 text-zinc-400 text-sm">{record.date || "-"}</td>
                  <td className="px-4 py-3 text-zinc-400 text-sm">{record.category || "-"}</td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => handleTogglePublish(record.id)}
                      className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded ${
                        record.is_published !== false
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-zinc-700 text-zinc-400'
                      }`}
                      data-testid={`toggle-publish-${record.id}`}
                    >
                      {record.is_published !== false ? (
                        <><Eye className="w-3 h-3" /> Published</>
                      ) : (
                        <><EyeOff className="w-3 h-3" /> Hidden</>
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEdit(record)}
                        className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded"
                        data-testid={`edit-record-${record.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(record.id)}
                        className="p-1.5 text-zinc-400 hover:text-red-500 hover:bg-zinc-800 rounded"
                        data-testid={`delete-record-${record.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* CSV Format Help */}
      <div className="bg-zinc-900/50 border border-white/10 p-4 text-sm">
        <h4 className="text-white font-bold mb-2 flex items-center gap-2">
          <FileSpreadsheet className="w-4 h-4" />
          CSV Import Format for {currentSport?.label}
        </h4>
        <p className="text-zinc-400 mb-2">
          Required columns: <code className="text-orange-500">Date, Player / Team, Total</code>
        </p>
        <p className="text-zinc-400">
          Optional columns: <code className="text-zinc-500">Record Category, Sport, Notes</code>
        </p>
        <p className="text-zinc-500 text-xs mt-2">
          The sport will be auto-set to &quot;{currentSport?.label}&quot; for all imported records. Download the template for the correct format.
        </p>
        <p className="text-zinc-500 text-xs mt-1">
          <strong>Tip:</strong> You can use your existing spreadsheets - the importer accepts both &quot;Player&quot; or &quot;Player / Team&quot; and &quot;Category&quot; or &quot;Record Category&quot;.
        </p>
      </div>
    </div>
  );
};

export default RecordBookTab;
